import { useState, useEffect } from 'react';
import {
  ContentLayout,
  Header,
  Button,
  Cards,
  Alert,
  Spinner,
  StatusIndicator,
} from '@cloudscape-design/components';
import { useConfigs, useRestoreConfig } from '@features/configs';
import { ConfigurationObject } from '@features/configs/services/configs-api';
import { useIsAdmin } from '@features/auth';
import { useNotifications } from '@features/notifications';

/**
 * Formats a countdown string from a future ISO timestamp.
 */
function formatCountdown(deletionScheduledAt: string): string {
  const remaining = new Date(deletionScheduledAt).getTime() - Date.now();
  if (remaining <= 0) return 'Deleting soon...';

  const hours = Math.floor(remaining / (1000 * 60 * 60));
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) return `${hours}h ${minutes}m remaining`;
  return `${minutes}m remaining`;
}

/**
 * Pending deletions page — shows configs awaiting permanent deletion
 * with countdown timers and cancel deletion option (admin-only).
 *
 * Requirements: 5.16, 5.17, 5.18, 5.19, 5.24
 */
export function PendingDeletionsPage() {
  const { data, isLoading, error } = useConfigs(true); // include pending deletions
  const restoreConfig = useRestoreConfig();
  const { isAdmin } = useIsAdmin();
  const { addNotification } = useNotifications();
  const [, setTick] = useState(0);

  // Update countdown every minute
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(interval);
  }, []);

  const pendingItems = (data?.items ?? []).filter(
    (item) => item.deletionScheduledAt
  );

  const handleRestore = async (config: ConfigurationObject) => {
    try {
      await restoreConfig.mutateAsync(config.configId);
      addNotification('success', `Deletion of '${config.configName}' cancelled`);
    } catch (e) {
      const { translateError } = await import('@features/notifications/error-messages');
      const translated = translateError(e, { configName: config.configName });
      addNotification('error', translated.message, translated.technicalDetails);
    }
  };

  if (isLoading) {
    return (
      <ContentLayout>
        <Spinner size="large" />
      </ContentLayout>
    );
  }

  if (error) {
    return (
      <ContentLayout>
        <Alert type="error">Unable to load pending deletions. Please retry.</Alert>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      header={
        <Header variant="h1" counter={`(${pendingItems.length})`}>
          Pending Deletions
        </Header>
      }
    >
      {pendingItems.length === 0 ? (
        <Alert type="info">No configurations are pending deletion.</Alert>
      ) : (
        <Cards
          items={pendingItems}
          cardDefinition={{
            header: (item) => item.configName,
            sections: [
              {
                id: 'type',
                header: 'Type',
                content: (item) => item.configType,
              },
              {
                id: 'deletedBy',
                header: 'Deleted by',
                content: (item) => item.deletedBy ?? 'Unknown',
              },
              {
                id: 'countdown',
                header: 'Time remaining',
                content: (item) => (
                  <StatusIndicator type="warning">
                    {formatCountdown(item.deletionScheduledAt!)}
                  </StatusIndicator>
                ),
              },
              {
                id: 'referencedBy',
                header: 'Referenced by',
                content: (item) => {
                  // Find all non-pending configs that reference this pending item
                  const allItems = data?.items ?? [];
                  const refs = allItems.filter((other) => {
                    if (other.configId === item.configId) return false;
                    if (other.deletionScheduledAt) return false;
                    if (!other.payload) return false;
                    return Object.values(other.payload).some(
                      (val) => typeof val === 'string' && val === item.configId
                    );
                  });
                  if (refs.length === 0) return 'None';
                  return refs.map((r) => r.configName).join(', ');
                },
              },
              ...(isAdmin
                ? [
                    {
                      id: 'actions',
                      content: (item: ConfigurationObject) => (
                        <Button
                          variant="normal"
                          loading={restoreConfig.isPending}
                          onClick={() => handleRestore(item)}
                        >
                          Cancel deletion
                        </Button>
                      ),
                    },
                  ]
                : []),
            ],
          }}
          cardsPerRow={[{ cards: 1 }, { minWidth: 500, cards: 2 }]}
        />
      )}
    </ContentLayout>
  );
}
